import { useState, useMemo } from 'react'
import { useSearchParams } from 'react-router-dom'
import { toast } from 'react-hot-toast'
import { Plus, Pencil, Trash2, Search, Loader2, Lock, Info } from 'lucide-react'
import {
  Card,
  CardContent,
  Button,
  Input,
  Label,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Badge,
  Separator,
  Textarea,
} from '@jira-clone/ui'
import { useTaxMasters, useTaxMasterMutations, TAX_TREATMENT_LABELS, type TaxTreatment, type TaxMaster } from '../../shared/hooks/useTaxMasters'
import { ConfirmDialog } from '@/shared/components/ui/ConfirmDialog'
import { getApiErrorMessage } from '../../shared/utils/apiError'
import { cn, cardClassName } from '@jira-clone/ui'

const TREATMENT_COLORS: Record<TaxTreatment, string> = {
  taxable: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300 border-blue-200 dark:border-blue-800',
  nil_rated: 'bg-slate-100 text-slate-800 dark:bg-slate-800/50 dark:text-slate-300 border-slate-200 dark:border-slate-700',
  exempt: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300 border-green-200 dark:border-green-800',
  non_gst: 'bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-300 border-orange-200 dark:border-orange-800',
}

const TREATMENT_HELP: Record<TaxTreatment, string> = {
  taxable: 'GST is charged at the rate below.',
  nil_rated: 'A taxable supply whose notified rate is 0%. No tax is shown on the invoice.',
  exempt: 'Exempted from GST by notification. No tax is shown on the invoice.',
  non_gst: 'Outside GST altogether (for example petrol, diesel, alcohol for human consumption).',
}

const RATE_PRESETS = [5, 18, 40, 0.25, 1.5, 3]
const FILTERS: Array<'all' | TaxTreatment> = ['all', 'taxable', 'nil_rated', 'exempt', 'non_gst']

const roundRate = (n: number) => Math.round((Number(n) || 0) * 1000) / 1000
const todayIso = () => new Date().toISOString().split('T')[0]
const isoDate = (v?: string) => (v ? new Date(v).toISOString().split('T')[0] : '')

interface DraftState {
  id?: string
  name: string
  code: string
  treatment: TaxTreatment
  rate: string
  effectiveFrom: string
  effectiveTo: string
  notificationRef: string
  helpText: string
  isActive: boolean
}

const EMPTY_DRAFT: DraftState = {
  name: '',
  code: '',
  treatment: 'taxable',
  rate: '18',
  effectiveFrom: todayIso(),
  effectiveTo: '',
  notificationRef: '',
  helpText: '',
  isActive: true,
}

/** What an invoice would show for this slab, worked out from the one rate. */
function SplitPreview({ treatment, rate }: { treatment: TaxTreatment; rate: number }) {
  if (treatment !== 'taxable') {
    return <p className="text-sm text-muted-foreground">{TAX_TREATMENT_LABELS[treatment]}: no tax is charged on the invoice.</p>
  }
  const half = roundRate(rate / 2)
  return (
    <div className="grid gap-2 sm:grid-cols-2 text-sm">
      <div className={cn(cardClassName({ variant: 'inset' }), 'p-3')}>
        <p className="text-xs font-medium text-muted-foreground">Sale within one State</p>
        <p className="font-semibold">CGST {half}% + SGST {half}%</p>
      </div>
      <div className={cn(cardClassName({ variant: 'inset' }), 'p-3')}>
        <p className="text-xs font-medium text-muted-foreground">Sale to another State</p>
        <p className="font-semibold">IGST {roundRate(rate)}%</p>
      </div>
    </div>
  )
}

export default function TaxMasterAdminPage() {
  const [searchParams, setSearchParams] = useSearchParams()
  const treatmentParam = searchParams.get('treatment') as TaxTreatment | 'all' | null
  const activeFilter: 'all' | TaxTreatment = treatmentParam && FILTERS.includes(treatmentParam) ? treatmentParam : 'all'
  const [search, setSearch] = useState(searchParams.get('search') || '')
  const [isActiveFilter, setIsActiveFilter] = useState<'all' | 'true' | 'false'>((searchParams.get('isActive') as any) || 'all')

  const updateParams = (next: { treatment?: string; search?: string; isActive?: string }) => {
    const params: Record<string, string> = {}
    const treatment = next.treatment ?? activeFilter
    const q = next.search ?? search
    const active = next.isActive ?? isActiveFilter
    if (treatment !== 'all') params.treatment = treatment
    if (q) params.search = q
    if (active !== 'all') params.isActive = active
    setSearchParams(params)
  }

  const { data: allTaxes = [], isLoading } = useTaxMasters({
    treatment: activeFilter === 'all' ? undefined : activeFilter,
    isActive: isActiveFilter === 'all' ? undefined : isActiveFilter === 'true',
  })
  const taxMasters = useMemo(() => {
    const q = search.trim().toLowerCase()
    return q ? allTaxes.filter((t) => `${t.name} ${t.code}`.toLowerCase().includes(q)) : allTaxes
  }, [allTaxes, search])

  const { create, update, setStatus, remove } = useTaxMasterMutations()

  const [dialogOpen, setDialogOpen] = useState(false)
  const [draft, setDraft] = useState<DraftState>(EMPTY_DRAFT)
  const [editing, setEditing] = useState<TaxMaster | null>(null)
  const [pendingDelete, setPendingDelete] = useState<TaxMaster | null>(null)

  // A slab that services or HSN/SAC codes already use keeps its rate, treatment and code.
  const locked = Boolean(editing && (editing.usageCount || 0) > 0)

  const openCreate = () => {
    setEditing(null)
    setDraft({ ...EMPTY_DRAFT, effectiveFrom: todayIso() })
    setDialogOpen(true)
  }

  const openEdit = (tax: TaxMaster) => {
    setEditing(tax)
    setDraft({
      id: tax.id,
      name: tax.name,
      code: tax.code || '',
      treatment: tax.treatment || 'taxable',
      rate: String(tax.rate ?? 0),
      effectiveFrom: isoDate(tax.effectiveFrom) || todayIso(),
      effectiveTo: isoDate(tax.effectiveTo),
      notificationRef: tax.notificationRef || '',
      helpText: tax.helpText || '',
      isActive: tax.isActive,
    })
    setDialogOpen(true)
  }

  const rateNumber = roundRate(parseFloat(draft.rate) || 0)

  const handleSave = async () => {
    if (draft.treatment === 'taxable' && !(rateNumber > 0 && rateNumber <= 100)) {
      toast.error('A taxable slab needs a GST rate above 0%. Use Nil-rated, Exempt or Non-GST for zero-tax supplies.')
      return
    }
    if (draft.effectiveTo && draft.effectiveFrom && draft.effectiveTo < draft.effectiveFrom) {
      toast.error('Effective To cannot be before Effective From.')
      return
    }
    try {
      const payload = {
        name: draft.name.trim() || undefined,
        code: draft.code.trim() || undefined,
        treatment: draft.treatment,
        rate: draft.treatment === 'taxable' ? rateNumber : 0,
        effectiveFrom: draft.effectiveFrom || undefined,
        effectiveTo: draft.effectiveTo || null,
        notificationRef: draft.notificationRef.trim(),
        helpText: draft.helpText.trim(),
        isActive: draft.isActive,
      }
      if (draft.id) {
        await update.mutateAsync({ id: draft.id, payload: payload as any })
        toast.success('Tax updated.')
      } else {
        await create.mutateAsync(payload as any)
        toast.success('Tax created.')
      }
      setDialogOpen(false)
    } catch (err) {
      toast.error(getApiErrorMessage(err, draft.id ? 'update this tax' : 'create this tax'))
    }
  }

  const handleDelete = async () => {
    if (!pendingDelete) return
    try {
      await remove.mutateAsync(pendingDelete.id)
      toast.success('Tax deleted.')
      setPendingDelete(null)
    } catch (err) {
      toast.error(getApiErrorMessage(err, 'delete this tax'))
    }
  }

  const handleToggleActive = async (tax: TaxMaster) => {
    try {
      await setStatus.mutateAsync({ id: tax.id, isActive: !tax.isActive })
      toast.success(tax.isActive ? 'Tax retired. It can no longer be picked for new services.' : 'Tax activated.')
    } catch (err) {
      toast.error(getApiErrorMessage(err, 'change this tax'))
    }
  }

  const namePlaceholder = draft.treatment === 'taxable' ? `GST ${rateNumber || 18}%` : TAX_TREATMENT_LABELS[draft.treatment]

  return (
    <div className="space-y-4 max-w-6xl">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-bold text-foreground">Tax Masters</h2>
          <p className="text-sm text-muted-foreground">GST slabs. Each has one rate; CGST, SGST and IGST are worked out on every invoice.</p>
        </div>
        <Button onClick={openCreate} className="gap-1.5">
          <Plus className="h-4 w-4" /> New Tax
        </Button>
      </div>

      <Card>
        <CardContent className="p-4 flex gap-3 text-sm">
          <Info className="h-4 w-4 mt-0.5 shrink-0 text-primary" />
          <p className="text-muted-foreground">
            You only set the <strong className="text-foreground">total GST rate</strong> (for example 18%). On a sale within the same State the invoice shows
            CGST 9% + SGST 9%; on a sale to another State it shows IGST 18%. Which one applies is decided by the customer's place of supply, so it is not stored here.
          </p>
        </CardContent>
      </Card>

      {/* Filters */}
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-wrap items-center gap-4">
            <div className="flex items-center gap-1 bg-muted p-1 rounded-lg">
              {FILTERS.map((f) => (
                <button
                  key={f}
                  onClick={() => updateParams({ treatment: f })}
                  className={`px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${
                    activeFilter === f ? 'bg-background text-foreground shadow-sm' : 'text-muted-foreground hover:text-foreground'
                  }`}
                >
                  {f === 'all' ? 'All' : TAX_TREATMENT_LABELS[f]}
                </button>
              ))}
            </div>

            <Separator className="h-6 w-px" />

            <div className="relative flex-1 min-w-[200px]">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search by name or code..."
                value={search}
                onChange={(e) => {
                  setSearch(e.target.value)
                  updateParams({ search: e.target.value })
                }}
                className="pl-9"
              />
            </div>

            <Select
              value={isActiveFilter}
              onValueChange={(v: 'all' | 'true' | 'false') => {
                setIsActiveFilter(v)
                updateParams({ isActive: v })
              }}
            >
              <SelectTrigger className="w-[140px]">
                <SelectValue placeholder="All Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All</SelectItem>
                <SelectItem value="true">Active</SelectItem>
                <SelectItem value="false">Retired</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {isLoading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      ) : taxMasters.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center">
            <p className="text-sm text-muted-foreground">No taxes match. Create one with New Tax.</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-2">
          {taxMasters.map((tax) => {
            const used = tax.usageCount || 0
            return (
              <Card key={tax.id} className={tax.isActive ? '' : 'opacity-70'}>
                <CardContent className="p-4">
                  <div className="flex items-center justify-between gap-3">
                    <div className="flex items-center gap-3 min-w-0 flex-1">
                      <div className="w-16 shrink-0 text-center">
                        <p className="text-lg font-bold text-foreground leading-tight">{tax.treatment === 'taxable' ? `${tax.rate}%` : '0%'}</p>
                        <p className="text-[10px] uppercase tracking-wide text-muted-foreground">GST</p>
                      </div>
                      <div className="min-w-0">
                        <div className="flex flex-wrap items-center gap-2">
                          <p className="text-sm font-semibold text-foreground truncate">{tax.name}</p>
                          <span className="font-mono text-[10px] px-1.5 py-0.5 rounded bg-muted text-muted-foreground">{tax.code}</span>
                          <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium border ${TREATMENT_COLORS[tax.treatment]}`}>
                            {TAX_TREATMENT_LABELS[tax.treatment]}
                          </span>
                          <Badge variant={tax.isActive ? 'default' : 'secondary'} className="text-[10px]">
                            {tax.isActive ? 'Active' : 'Retired'}
                          </Badge>
                        </div>
                        <p className="text-xs text-muted-foreground mt-0.5">{tax.preview}</p>
                        <p className="text-[11px] text-muted-foreground/80 mt-0.5 flex flex-wrap items-center gap-x-3">
                          {tax.effectiveFrom && <span>From {new Date(tax.effectiveFrom).toLocaleDateString('en-IN')}</span>}
                          {tax.effectiveTo && <span>To {new Date(tax.effectiveTo).toLocaleDateString('en-IN')}</span>}
                          {tax.notificationRef && <span>{tax.notificationRef}</span>}
                          <span>{used > 0 ? `Used by ${used} service${used === 1 ? '' : 's'} / HSN code${used === 1 ? '' : 's'}` : 'Not used yet'}</span>
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-1 shrink-0">
                      <Button variant="ghost" size="icon" onClick={() => openEdit(tax)} title={used > 0 ? 'Edit (rate is locked while in use)' : 'Edit'}>
                        {used > 0 ? <Lock className="h-3.5 w-3.5" /> : <Pencil className="h-3.5 w-3.5" />}
                      </Button>
                      <Button variant="ghost" size="icon" onClick={() => handleToggleActive(tax)} title={tax.isActive ? 'Retire' : 'Activate'}>
                        {tax.isActive ? <span className="h-3.5 w-3.5 rounded-full bg-green-500" /> : <span className="h-3.5 w-3.5 rounded-full bg-gray-400" />}
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => setPendingDelete(tax)}
                        disabled={used > 0}
                        title={used > 0 ? `Used by ${used} service${used === 1 ? '' : 's'} / HSN code${used === 1 ? '' : 's'} - retire it instead` : 'Delete'}
                      >
                        {used > 0 ? <Lock className="h-3.5 w-3.5 text-muted-foreground" /> : <Trash2 className="h-3.5 w-3.5 text-destructive" />}
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )
          })}
        </div>
      )}

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-2xl">
          <DialogHeader>
            <DialogTitle>{draft.id ? 'Edit Tax' : 'New Tax'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-2 max-h-[70vh] overflow-y-auto">
            {locked && (
              <p className="flex gap-2 rounded-md border border-amber-300 bg-amber-50 p-3 text-xs text-amber-900 dark:border-amber-800 dark:bg-amber-950/40 dark:text-amber-200">
                <Lock className="h-4 w-4 shrink-0 mt-px" />
                <span>
                  {editing?.name} is used by {editing?.usageCount} service(s) / HSN code(s), so its rate, treatment and code are locked. To change the rate,
                  retire this tax and create a new one.
                </span>
              </p>
            )}

            <div className="grid gap-4 md:grid-cols-2">
              <div className="space-y-1.5">
                <Label htmlFor="tax-treatment">Treatment *</Label>
                <Select value={draft.treatment} onValueChange={(v) => setDraft((d) => ({ ...d, treatment: v as TaxTreatment }))} disabled={locked}>
                  <SelectTrigger id="tax-treatment">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {(Object.keys(TAX_TREATMENT_LABELS) as TaxTreatment[]).map((t) => (
                      <SelectItem key={t} value={t}>
                        {TAX_TREATMENT_LABELS[t]}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-xs text-muted-foreground">{TREATMENT_HELP[draft.treatment]}</p>
              </div>

              <div className="space-y-1.5">
                <Label htmlFor="tax-rate">GST Rate (%) {draft.treatment === 'taxable' ? '*' : ''}</Label>
                <Input
                  id="tax-rate"
                  type="number"
                  min={0}
                  max={100}
                  step={0.001}
                  value={draft.treatment === 'taxable' ? draft.rate : '0'}
                  onChange={(e) => setDraft((d) => ({ ...d, rate: e.target.value }))}
                  disabled={locked || draft.treatment !== 'taxable'}
                  placeholder="18"
                />
                {draft.treatment === 'taxable' && !locked && (
                  <div className="flex flex-wrap gap-1.5 pt-0.5">
                    {RATE_PRESETS.map((p) => (
                      <button
                        key={p}
                        type="button"
                        onClick={() => setDraft((d) => ({ ...d, rate: String(p) }))}
                        className={`px-2 py-0.5 rounded border text-xs ${rateNumber === p ? 'bg-primary text-primary-foreground border-primary' : 'bg-muted hover:bg-muted/70'}`}
                      >
                        {p}%
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>

            <div className="rounded-lg bg-muted/50 p-3 space-y-2">
              <p className="text-xs font-medium text-muted-foreground">What the invoice will show (worked out automatically)</p>
              <SplitPreview treatment={draft.treatment} rate={rateNumber} />
            </div>

            <div className="grid gap-4 md:grid-cols-2">
              <div className="space-y-1.5">
                <Label htmlFor="tax-name">Name</Label>
                <Input id="tax-name" value={draft.name} onChange={(e) => setDraft((d) => ({ ...d, name: e.target.value }))} placeholder={namePlaceholder} />
                <p className="text-xs text-muted-foreground">Leave empty to use “{namePlaceholder}”.</p>
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="tax-code">Code</Label>
                <Input
                  id="tax-code"
                  value={draft.code}
                  onChange={(e) => setDraft((d) => ({ ...d, code: e.target.value.toUpperCase() }))}
                  disabled={locked}
                  placeholder="Filled automatically, e.g. GST18"
                />
                <p className="text-xs text-muted-foreground">A short unique key. Letters, digits, dot or underscore.</p>
              </div>
            </div>

            <Separator className="my-2" />

            <h4 className="text-sm font-medium text-foreground">Validity</h4>
            <div className="grid gap-4 md:grid-cols-2">
              <div className="space-y-1.5">
                <Label htmlFor="tax-effective-from">Effective From *</Label>
                <Input id="tax-effective-from" type="date" value={draft.effectiveFrom} onChange={(e) => setDraft((d) => ({ ...d, effectiveFrom: e.target.value }))} />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="tax-effective-to">Effective To</Label>
                <Input id="tax-effective-to" type="date" value={draft.effectiveTo} onChange={(e) => setDraft((d) => ({ ...d, effectiveTo: e.target.value }))} />
              </div>
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="tax-notification">Notification reference</Label>
              <Input
                id="tax-notification"
                value={draft.notificationRef}
                onChange={(e) => setDraft((d) => ({ ...d, notificationRef: e.target.value }))}
                placeholder="Optional, e.g. 9/2025-CT(Rate), 17 Sep 2025"
              />
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="tax-help">Notes</Label>
              <Textarea
                id="tax-help"
                value={draft.helpText}
                onChange={(e) => setDraft((d) => ({ ...d, helpText: e.target.value }))}
                rows={2}
                placeholder="Optional. Shown to your team, e.g. “Standard rate for IT services”."
              />
            </div>

            <div className="flex items-center gap-2">
              <Label className="flex items-center gap-2 text-sm cursor-pointer">
                <input type="checkbox" checked={draft.isActive} onChange={(e) => setDraft((d) => ({ ...d, isActive: e.target.checked }))} />
                <span>Active (can be picked for new services)</span>
              </Label>
            </div>
          </div>
          <DialogFooter className="mt-4">
            <Button variant="outline" onClick={() => setDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleSave} disabled={create.isPending || update.isPending}>
              {draft.id ? 'Save Changes' : 'Create Tax'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <ConfirmDialog
        open={!!pendingDelete}
        onCancel={() => setPendingDelete(null)}
        onConfirm={handleDelete}
        loading={remove.isPending}
        danger
        title={`Delete "${pendingDelete?.name || ''}"?`}
        message="The slab disappears from the tax dropdowns for new services and HSN/SAC codes. Documents already raised with it keep the rate they were made with."
        confirmLabel="Delete tax"
      />
    </div>
  )
}
